import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useParams, useNavigate } from "react-router-dom";
import { useUser } from "@/context/UserContext";
import api, { getWsUrl } from "@/utils/api";
import {
  ArrowLeft, Send, Paperclip, Image, FileText, Video,
  CheckCheck, Clock, X, Maximize2, Download
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Animation presets (extracted to avoid inline object recreation)
const BUBBLE_ANIM = {
  initial: { opacity: 0, y: 12, scale: 0.95 },
  animate: { opacity: 1, y: 0, scale: 1 },
  transition: { type: "spring", stiffness: 400, damping: 30 },
};
const HEADER_ANIM = {
  initial: { opacity: 0, y: -20 },
  animate: { opacity: 1, y: 0 },
};
const INPUT_ANIM = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};
const EMPTY_ANIM = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
};

function MessageBubble({ msg, isMine }) {
  const isFile = msg.message_type !== "text";
  const isImage = ["image", "photo"].includes(msg.message_type);
  const isVideo = msg.message_type === "video";
  const isPdf = msg.message_type === "pdf";

  return (
    <motion.div
      {...BUBBLE_ANIM}
      className={`flex ${isMine ? "justify-end" : "justify-start"} mb-3`}
    >
      <div className={`max-w-xs lg:max-w-md xl:max-w-lg ${isMine ? "items-end" : "items-start"} flex flex-col`}>
        {/* Bubble */}
        <div
          className={`relative px-4 py-3 rounded-2xl shadow-sm ${
            isMine
              ? "bg-gradient-to-br from-[#14B8A6] to-[#0D9488] text-gray-900 rounded-br-md"
              : "glass-card text-gray-900 rounded-bl-md"
          }`}
          style={!isMine ? {
            background: "rgba(255,255,255,0.75)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(245,197,24,0.2)",
          } : {}}
        >
          {isImage && msg.file_url && (
            <img src={`${BACKEND_URL}${msg.file_url}`} alt="Bild" className="rounded-xl max-w-full max-h-48 object-cover mb-2" />
          )}
          {isVideo && msg.file_url && (
            <video src={`${BACKEND_URL}${msg.file_url}`} controls className="rounded-xl max-w-full max-h-48 mb-2" />
          )}
          {isPdf && msg.file_url && (
            <a href={`${BACKEND_URL}${msg.file_url}`} target="_blank" rel="noreferrer"
              className="flex items-center gap-2 p-3 bg-white/50 rounded-xl mb-2 hover:bg-white/80 transition-colors">
              <FileText size={20} className="text-red-500 flex-shrink-0" />
              <span className="text-sm font-medium truncate">{msg.file_name || "Dokument"}</span>
              <Download size={14} className="flex-shrink-0" />
            </a>
          )}
          {msg.content && <p className="text-sm leading-relaxed">{msg.content}</p>}
          <div className={`flex items-center gap-1 mt-1 ${isMine ? "justify-end" : "justify-start"}`}>
            <span className={`text-xs ${isMine ? "text-gray-700/70" : "text-gray-400"}`}>
              {new Date(msg.created_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
            </span>
            {isMine && <CheckCheck size={12} className="text-gray-700/70" />}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function Chat() {
  const { requestId } = useParams();
  const { user } = useUser();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [request, setRequest] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [uploading, setUploading] = useState(false);
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileRef = useRef(null);
  const imageRef = useRef(null);
  const videoRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load initial data
  useEffect(() => {
    api.get(`/requests/${requestId}`).then((r) => setRequest(r.data)).catch(() => {});
    api.get(`/messages/${requestId}`).then((r) => setMessages(r.data)).catch(() => {});
  }, [requestId]);

  // WebSocket
  useEffect(() => {
    const wsUrl = getWsUrl(requestId);
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => setWsConnected(true);
    ws.onclose = () => setWsConnected(false);
    ws.onerror = () => setWsConnected(false);
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "new_message") {
          setMessages((prev) => {
            const exists = prev.some((m) => m.id === data.message.id);
            return exists ? prev : [...prev, data.message];
          });
        }
      } catch {
        /* malformed payload, ignore */
      }
    };
    return () => ws.close();
  }, [requestId]);

  const sendMessage = useCallback(() => {
    if (!input.trim()) return;
    const msg = {
      sender_id: user.id,
      content: input.trim(),
      message_type: "text",
    };
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    } else {
      api.post("/messages", { request_id: requestId, ...msg }).then((r) => {
        setMessages((prev) => [...prev, r.data]);
      });
    }
    setInput("");
  }, [input, user.id, requestId]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleFileUpload = async (file, type) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const msg = {
        sender_id: user.id,
        content: file.name,
        message_type: type,
        file_url: res.data.url,
        file_name: file.name,
      };
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify(msg));
      } else {
        const r = await api.post("/messages", { request_id: requestId, ...msg });
        setMessages((prev) => [...prev, r.data]);
      }
    } catch (e) {
      alert("Upload fehlgeschlagen");
    } finally {
      setUploading(false);
    }
  };

  let otherName = "Chat";
  if (request) {
    otherName = user.user_type === "client" ? request.interpreter_name : request.client_name;
  }

  return (
    <div
      className="h-screen flex flex-col overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #f0fdfa 0%, #ffffff 40%, #f0f9ff 100%)",
      }}
    >
      {/* Ambient glow */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#14B8A6]/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-[#14B8A6]/8 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <motion.div
        {...HEADER_ANIM}
        className="relative z-10 flex items-center gap-4 px-4 py-3 border-b"
        style={{
          background: "rgba(255,255,255,0.8)",
          backdropFilter: "blur(20px)",
          borderColor: "rgba(245,197,24,0.2)",
        }}
      >
        <button
          onClick={() => navigate(-1)}
          data-testid="chat-back-btn"
          className="p-2 rounded-xl hover:bg-gray-100 transition-colors"
        >
          <ArrowLeft size={20} className="text-gray-700" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-[#14B8A6] rounded-xl flex items-center justify-center text-sm font-bold text-gray-900">
              {user.user_type === "client" ? "🤟" : "👂"}
            </div>
            <div>
              <p className="font-bold text-gray-900 leading-none text-sm">{otherName}</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <div className={`w-1.5 h-1.5 rounded-full ${wsConnected ? "bg-green-400" : "bg-gray-300"}`} />
                <span className="text-xs text-gray-500">{wsConnected ? "Verbunden" : "Nicht verbunden"}</span>
              </div>
            </div>
          </div>
        </div>
        {request && (
          <div className="text-right hidden sm:block">
            <p className="text-xs font-medium text-[#0D9488]">{request.event_type}</p>
            <p className="text-xs text-gray-400">{request.event_date}</p>
          </div>
        )}
      </motion.div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-6 relative z-10" data-testid="messages-container">
        {messages.length === 0 && (
          <motion.div
            {...EMPTY_ANIM}
            className="text-center py-12"
          >
            <div className="w-16 h-16 bg-[#FEF3CE] rounded-full flex items-center justify-center mx-auto mb-4 text-2xl">🤟</div>
            <p className="text-gray-500 text-sm font-medium">Chat gestartet</p>
            <p className="text-gray-400 text-xs mt-1">Schreibe eine Nachricht, um zu beginnen.</p>
          </motion.div>
        )}

        {messages.map((msg) => (
          <MessageBubble
            key={msg.id}
            msg={msg}
            isMine={msg.sender_id === user.id}
          />
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <motion.div
        {...INPUT_ANIM}
        className="relative z-10 p-4 border-t"
        style={{
          background: "rgba(255,255,255,0.85)",
          backdropFilter: "blur(20px)",
          borderColor: "rgba(245,197,24,0.2)",
        }}
      >
        {uploading && (
          <div className="text-xs text-[#0D9488] mb-2 flex items-center gap-2">
            <div className="w-3 h-3 border border-[#0D9488] border-t-transparent rounded-full animate-spin" />
            Datei wird hochgeladen...
          </div>
        )}
        <div
          className="flex items-end gap-3 rounded-2xl px-4 py-3"
          style={{
            background: "rgba(255,255,255,0.7)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(245,197,24,0.3)",
            boxShadow: "0 4px 24px rgba(245,197,24,0.1)",
          }}
        >
          {/* Attachment buttons */}
          <div className="flex gap-1 flex-shrink-0">
            <button
              onClick={() => imageRef.current?.click()}
              data-testid="attach-image-btn"
              className="p-2 rounded-xl text-gray-400 hover:text-[#0D9488] hover:bg-[#FEF3CE] transition-all"
              title="Bild senden"
            >
              <Image size={18} />
            </button>
            <button
              onClick={() => videoRef.current?.click()}
              data-testid="attach-video-btn"
              className="p-2 rounded-xl text-gray-400 hover:text-[#0D9488] hover:bg-[#FEF3CE] transition-all"
              title="Video senden"
            >
              <Video size={18} />
            </button>
            <button
              onClick={() => fileRef.current?.click()}
              data-testid="attach-pdf-btn"
              className="p-2 rounded-xl text-gray-400 hover:text-[#0D9488] hover:bg-[#FEF3CE] transition-all"
              title="PDF senden"
            >
              <FileText size={18} />
            </button>
          </div>

          {/* Hidden file inputs */}
          <input ref={imageRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFileUpload(e.target.files[0], "image")} />
          <input ref={videoRef} type="file" accept="video/*" className="hidden" onChange={(e) => handleFileUpload(e.target.files[0], "video")} />
          <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => handleFileUpload(e.target.files[0], "pdf")} />

          {/* Text input */}
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Nachricht schreiben..."
            rows={1}
            data-testid="chat-input"
            className="flex-1 resize-none bg-transparent text-sm text-gray-900 placeholder-gray-400 focus:outline-none leading-relaxed"
            style={{ maxHeight: "120px" }}
            onInput={(e) => {
              e.target.style.height = "auto";
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
            }}
          />

          {/* Send button */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={sendMessage}
            disabled={!input.trim()}
            data-testid="send-message-btn"
            className="flex-shrink-0 w-10 h-10 bg-[#14B8A6] rounded-xl flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#0D9488] transition-colors shadow-sm"
          >
            <Send size={18} className="text-gray-900" />
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
}
